/* agmf-track.js — what the shopper actually saw, and what they touched.
   ─────────────────────────────────────────────────────────────────────────
   Dorian, 2026-09-03. Three questions the existing analytics cannot answer:

     "Content ID, time this content appeared on the user screen, and time it
      appeared WITHOUT the user scrolling."
     "All clicks: any buttons, any sections, what cards they click, titles,
      the YouTube controls."
     "Track the cards' position within the feed / product card, as a matrix
      (x,y), so we know if they clicked or arrived at content that is buried
      in the swipeable cards (side scrolling)."
     "Track differently the 2 different add-to-cart buttons, the variant
      selector actions."

   WHY A SEPARATE FILE. cae-client.js is the shared emitter for every page of
   three storefronts; this is one page's instrumentation. It reads the feed
   through the engine's published hooks and the stamps already on the markup,
   and sends through `CAEDev.emit`, which is the same path every other event
   takes - the envelope (event id, session, viewer, page, device, batching,
   flush on unload) and every kill switch come with it, including the one that
   makes an internal session emit nothing at all.

   TIER A, so nothing else has to move: every field below rides inside the
   `detail` JSON of the event, which the ingest service serialises whole. No
   BigQuery column, no ingest deploy, no schema migration. Read them with
   JSON_VALUE(detail,'$.field') on `cae_customer_analytics.events_dedup`.

   THE EVENTS
     agmf_content_view  one per piece of content per visit to the screen:
                        what it was, where it sat, how long it was on screen,
                        and how much of that time the page was standing still.
     agmf_click         every click on the page, named. Content clicks carry
                        the same identity and position as the view above;
                        every click carries the theme section it landed in.
     agmf_rail_move     a side-scrolling card came to rest on another slide -
                        which slide, from which, in which direction, how far
                        the gesture travelled and how long the slide it left
                        had held them. The rail's FIRST settle is not sent:
                        a rail existing is not a shopper doing something.
     agmf_open          content was opened into the dialog or full screen -
                        what, from where, and in which layout.
     agmf_close         and how it was let go: the close button, Escape, the
                        backdrop - with how long it was held.
     agmf_ask_click     the Ask card's own buttons. A SEPARATE NAME on
                        purpose: these carry the card's own fields and none
                        of the page position, so folding them into
                        agmf_click gave one event name two shapes and any
                        query that assumed a surface silently dropped them.
     agmf_agilike       a double tap: the brand's like, with who and what.
     agmf_media_error   a picture or a clip that would not load.
     agmf_video_state   a clip started, paused or finished.
     agmf_add_to_cart   something went into the cart, and WHICH BUTTON did it
                        (Dorian 2026-09-06). The page has two: the product
                        form's own and the sticky bar's; they submit the same
                        form, so the add itself cannot say. The last trusted
                        press can.
     agmf_variant_drawer  the sticky bar's variant sheet: opened, a choice
                        made, applied, or refused because that combination
                        does not exist or is sold out.
     agmf_ask_offer     the Ask card put an add-to-cart button under an
                        answer: which variant, at what price, for which
                        question (qa_id joins it to the chat log).
     agmf_add_to_cart   ...also fires for the Ask card's own button, with
                        control = 'ask_card' and the qa_id (Dorian
                        2026-09-06: "Add the buy tracking to the events too").
     agmf_ask_add_failed  the card pressed, the cart refused.

   EVERY ROW CARRIES product_handle. The ingest promotes it to the table's own
   column, and without it no feed event could be joined to the product it
   was seen against (audit 2026-09-06).

   TIME, HONESTLY MEASURED. Dwell is accumulated from wall-clock deltas in a
   250ms sampler, and only while the piece is at least half on screen, the tab
   is visible, and no dialog covers the page. "Still" time is the part of that
   with no scrolling of any kind in the previous 200ms - the window, either
   desktop pane, or a rail. So a card that sits under the thumb during a long
   scroll does not bank attention it never had.

   AND SHARED OUT WHEN SEVERAL PIECES ARE ON SCREEN AT ONCE (2026-09-22).
   `ms_on_screen` gives every visible piece the whole of every tick, so on a
   desktop - two gallery slides side by side, a two-column feed - four
   seconds of looking became four seconds for each of them: eight seconds
   of attention nobody paid (Dorian: "desktop and mobile should have the
   same on screen"). `ms_share` splits each tick among the pieces at least
   half on screen, in proportion to how much of each is showing, so the
   shares always add up to the time that passed; `ms_still_share` is the
   same split of the still time; `peers` is the most pieces that were ever
   on screen together during the visit. On a phone one piece is on screen at
   a time and the share equals the old number. Both are sent; the warehouse
   reads the share where it exists and the old field where it does not.

   THE RECYCLER REBUILDS, IT DOES NOT REUSE. A block leaving the runway has
   its children destroyed and built again on return, so an element never
   carries a second piece of content - the guard for that is kept as a
   belt-and-braces check, but the live path is "the element left the DOM".
   What that DOES mean: scrolling away and back is a NEW element and a NEW
   row. So one row is ONE VISIT, `visit` counts them per piece per page, and
   "how many times did they see it" is COUNT(rows) GROUP BY ck - never a
   field called `enters`, which is why there is no such field.
   ───────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  if (window.__agmfTrack) return;
  window.__agmfTrack = true;

  var SAMPLE_MS = 250;      // how often dwell is banked
  var STILL_AFTER = 200;    // ms of no scrolling before the page counts as still
  var SEEN_RATIO = 0.5;     // half on screen is "on screen" (matches card_seen)
  var MIN_VIEW_MS = 300;    // shorter than this is a scroll-past, not a view
  var LONG_VIEW_MS = 60000; // bank a partial row for very long dwells
  var MAX_VIEWS = 300;      // per page: the event queue is shared with add_to_cart

  var t0 = Date.now();
  var lastScrollAt = 0;
  var records = new Map();  // element -> record
  var pending = [];         // events emitted before cae-client was ready
  var railSeq = 0;

  // The section stamps the product; the address is the fallback.
  var productHandle = (function () {
    var sec = document.querySelector('[data-agmf][data-product-handle]');
    var h = sec && sec.getAttribute('data-product-handle');
    if (h) return h;
    var m = (location.pathname || '').match(/\/products\/([^\/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }());

  // Which theme section an element sits in, by the wrapper the theme
  // renders around every section. The type is the wrapper's own class
  // token where it has one, else the section root's declaration.
  function sectionOf(el) {
    var wrap = el && el.closest ? el.closest('[id^="shopify-section-"]') : null;
    if (!wrap) return { section_id: null, section_type: null };
    var type = null;
    var m = (wrap.className || '').match(/shopify-section--([\w-]+)/);
    if (m) type = m[1];
    else {
      var inner = wrap.querySelector('[data-section-type]');
      type = inner ? inner.getAttribute('data-section-type') : null;
    }
    return { section_id: wrap.id.replace(/^shopify-section-/, ''), section_type: type };
  }

  /* ── the wire ───────────────────────────────────────────────────────── */

  function emit(name, detail) {
    if (detail && productHandle && detail.product_handle == null) detail.product_handle = productHandle;
    // the link as the engine sees it ('constrained' | 'open'): whether the
    // radio was in the way is the first question of any 4G complaint
    if (detail && detail.link == null) {
      try { detail.link = (window.AGMF && window.AGMF.link) ? window.AGMF.link() : null; } catch (e) { detail.link = null; }
    }
    // THE LAYOUT IS STAMPED AS THE EVENT LEAVES, not as it is made, and
    // nothing leaves before the page has said which layout it is (see
    // settleLayout below). A record opened before the desktop switch
    // arrived used to carry the width's answer for good.
    var dev = window.CAEDev;
    if (layout && dev && typeof dev.emit === 'function') { send(dev, name, detail); return; }
    if (pending.length < 400) pending.push([name, detail]);
  }
  function send(dev, name, detail) {
    // the band belongs to every event, not to the nine that remembered to
    // paste it - a click means a different gesture on each of the four
    if (detail && detail.layout == null) detail.layout = layout;
    try { dev.emit(name, detail); } catch (e) {}
  }
  function drainNow() {
    var dev = window.CAEDev;
    if (!layout || !dev || typeof dev.emit !== 'function' || !pending.length) return;
    var q = pending; pending = [];
    for (var i = 0; i < q.length; i++) send(dev, q[i][0], q[i][1]);
  }
  (function drain() {
    drainNow();
    if (Date.now() - t0 < 30000) setTimeout(drain, 500);
  }());

  /* ── where we are ───────────────────────────────────────────────────── */

  function desktop() { return document.documentElement.classList.contains('agdesk-on'); }

  /* ── WHICH LAYOUT THIS IS, by the name a person would use (Dorian
     2026-09-07: "in analytics we should have a way to know on what kind of
     layout it was"). It was a two-valued phone/desktop flag written out
     longhand in ten places; there are FOUR bands and they behave differently:

       phone         < 760    one column, the product block stacked
       tablet        760-999  the feed becomes a 640px centred column
       tablet_large  1000-1199 the THEME turns the product side-by-side and
                               makes the info column sticky - still one feed
                               column, and `agdesk-on` is NOT on yet
       desktop       >= 1200  our own three-column page; the feed is moved
                               beside the buy column

     `agdesk-on` is asked FIRST and on purpose. It is the real switch, and it
     can fail to arrive even above 1200 - pdp-desk-3col.js only adds it once
     it has found every piece of the page, so a page whose hero never builds
     stays on the narrow layout at 1400px wide. Reporting the width there
     would file those sessions under a layout the shopper never saw. */
  function widthBand() {
    var w = window.innerWidth || 0;
    if (w < 760) return 'phone';
    if (w < 1000) return 'tablet';
    return 'tablet_large';
  }

  /* ONE LAYOUT PER PAGE LOAD, decided when the page can say (2026-09-22).
     Measured over 7 days: 695 of 4,656 visits carried both `tablet_large`
     and `desktop` - 662 of them at ONE window width. Nobody resized
     anything: the desktop switch arrives after pdp-desk-3col has found
     every piece of the page, and every event before that moment was filed
     by width, under a layout the shopper never saw (Dorian: "how can it be
     that one session have multiple layouts"). So on a wide window the
     answer is WAITED FOR - up to LAYOUT_WAIT_MS, then the honest one: the
     desktop script never claimed the page and it really is the wide narrow
     layout. `emit` holds every event until then, and the unload flush
     decides on the spot rather than lose a short visit. A change after
     that - a rotation, a window dragged across a band - is a real change
     and goes out as one, agmf_layout_change. */
  var LAYOUT_WAIT_MS = 3000;
  var layout = null;
  var layoutTimer = 0;
  function layoutName() { return layout; }
  function settleLayout(why) {
    var next = desktop() ? 'desktop' : widthBand();
    if (!layout && next === 'tablet_large' && why !== 'timeout' && why !== 'unload') {
      // wide and unclaimed: the desktop switch may still be on its way
      if (!layoutTimer) {
        layoutTimer = setTimeout(function () { layoutTimer = 0; settleLayout('timeout'); }, LAYOUT_WAIT_MS);
      }
      return;
    }
    if (layoutTimer) { clearTimeout(layoutTimer); layoutTimer = 0; }
    if (!layout) { layout = next; drainNow(); return; }
    if (next !== layout) {
      var was = layout;
      layout = next;
      emit('agmf_layout_change', {
        from: was, to: next, why: why,
        viewport_w: window.innerWidth, viewport_h: window.innerHeight,
        ms_since_load: Date.now() - t0
      });
    }
  }
  settleLayout('first');

  /* THE DESKTOP ARRANGES THE PAGE AFTER THE FIRST LOOKS ARE MEASURED
     (2026-09-23). A look's place is measured once, when the piece comes on
     screen. pdp-desk-3col.js moves the hero and the feed into the media pane
     and turns them into two-column grids some time after they are built, so
     the first looks of a desktop visit carried the geometry of a moment that
     did not last: 83 of 127 desktop visits since 22 Sep reported the product
     card as one twelve-wide row - a rail, measured just before it became a
     grid - and the feed's rows were counted one column wide. When the html
     class changes (agdesk-on, agdesk-wide arriving or leaving), every open
     look is measured again and the per-block row counts are forgotten, so
     the rows down the feed are counted in the geometry that is there. */
  var arranged = '';
  function remeasure() {
    var cls = document.documentElement.classList;
    var now = (cls.contains('agdesk-on') ? 'on ' : '') + (cls.contains('agdesk-wide') ? 'wide' : '');
    if (now === arranged) return;
    arranged = now;
    blockRows = {};
    records.forEach(function (rec) {
      if (rec.el && document.documentElement.contains(rec.el)) rec.place = placeOf(rec.el);
    });
  }
  try {
    new MutationObserver(function () { settleLayout('class'); remeasure(); })
      .observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  } catch (e) { /* no observer: the wait alone decides */ }
  var resizeTimer = 0;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { settleLayout('resize'); }, 300);
  });
  // A dialog covers the page - but not itself. Counting its own content as
  // hidden made the highest-intent surface on the page record zero time.
  function covered(el) {
    if (!document.documentElement.classList.contains('agmf-noscroll')) return false;
    return !(el && el.closest && el.closest('.agmf-lb, .agmf-art-dialog'));
  }
  function still() { return Date.now() - lastScrollAt > STILL_AFTER; }

  function surfaceOf(el) {
    if (el.closest('.agmf-lb')) return 'dialog';
    if (el.closest('.agmf-hero')) return 'product_card';
    if (el.closest('.agmf-feed, .agmf-cycle')) return 'feed';
    return 'page';
  }

  /* ── what a piece of content is ─────────────────────────────────────── */

  // The engine stamps the feed's own cards (ck / type / id / idx / lap) and
  // hangs the whole record on any element it renders, rail slides included -
  // those get the record but no stamps, so both are read here.
  function identity(el) {
    var out = { ck: null, content_type: null, record_id: null, platform: null, format: null };
    if (!el) return out;
    var item = el.__agmfItem;
    if (!item) {
      var host = el.closest('[data-agmf-ck], [data-agmf-track-ck], .agmf-card, .agmf-hero-slide');
      if (host) item = host.__agmfItem;
      if (host && host.dataset) {
        // the product's own photographs carry their key in their own
        // attribute - the seen ledger's `ck` space stays library-only
        out.ck = host.dataset.agmfCk || host.dataset.agmfTrackCk || null;
        out.content_type = host.dataset.agmfType || host.dataset.agmfTrackType || null;
        out.record_id = host.dataset.agmfId || null;
      }
    }
    if (item) {
      out.ck = item.ck || out.ck;
      out.content_type = item.contentType || out.content_type;
      out.record_id = item.id != null ? String(item.id) : out.record_id;
      out.platform = item.platform || null;
      out.format = item.format || null;
    }
    return out;
  }

  /* ── the matrix ─────────────────────────────────────────────────────── */

  // (x, y) is asked of the LAYOUT, not of the feed order: the desktop grid
  // packs densely and a video takes a whole row, so counting cards would put
  // a card in a column it is not in. Rows are the distinct top edges in the
  // container, columns the rank of a cell's left edge within its row - true
  // on a phone (one column), on the two-column desktop grid, and inside a
  // dialog. Measured once when a piece comes on screen, not every sample.
  var blockRows = {};   // feed block index -> rows it holds, learned as each is seen
  function gridCell(node) {
    var box = node.closest('.agmf-cycle, .agmf-hero-track, .agmf-feed');
    if (!box) return { col: null, row: null, of_cols: null };
    var kids = [], i, r;
    for (i = 0; i < box.children.length; i++) {
      r = box.children[i].getBoundingClientRect();
      if (r.width > 1 && r.height > 1) kids.push({ el: box.children[i], top: Math.round(r.top), left: Math.round(r.left) });
    }
    var mine = node.closest('.agmf-cycle > *, .agmf-hero-track > *') || node;
    while (mine && mine.parentElement !== box) mine = mine.parentElement;
    if (!mine) return { col: null, row: null, of_cols: null };
    var myTop = Math.round(mine.getBoundingClientRect().top);
    var rows = [];
    kids.forEach(function (k) {
      var row = rows.find(function (x) { return Math.abs(x.top - k.top) < 6; });
      if (!row) { row = { top: k.top, cells: [] }; rows.push(row); }
      row.cells.push(k);
    });
    rows.sort(function (a, b) { return a.top - b.top; });
    var rowIdx = rows.findIndex(function (x) { return Math.abs(x.top - myTop) < 6; });
    var row = rows[rowIdx];
    if (!row) return { col: null, row: null, of_cols: null };
    row.cells.sort(function (a, b) { return a.left - b.left; });
    var colIdx = row.cells.findIndex(function (c) { return c.el === mine; });
    // THE ROW DOWN THE WHOLE FEED, not down this block (Dorian 2026-09-22:
    // "how can it be 11 rows only??"). `row` counts inside one .agmf-cycle,
    // and a feed is many of them stacked. The blocks are built in order as
    // the shopper scrolls, so by the time a card in block 3 is on screen
    // blocks 0-2 have each been measured here and their row counts are
    // known; an earlier block never measured means no absolute row, said as
    // null rather than guessed. Both 1-based, like x and y.
    var gridRow = null;
    if (box.classList.contains('agmf-cycle') && box.parentElement) {
      var blockIdx = Array.prototype.indexOf.call(box.parentElement.querySelectorAll('.agmf-cycle'), box);
      if (blockIdx >= 0) {
        blockRows[blockIdx] = rows.length;
        var sum = 0, known = true;
        for (var b = 0; b < blockIdx; b++) {
          if (blockRows[b] == null) { known = false; break; }
          sum += blockRows[b];
        }
        if (known) gridRow = sum + rowIdx + 1;
      }
    }
    return { col: colIdx < 0 ? null : colIdx, row: rowIdx, of_cols: row.cells.length,
             grid_row: gridRow, grid_col: colIdx < 0 ? null : colIdx + 1 };
  }

  // A rail is the side-scrolling part: which slide this is, how many there
  // are, and whether it is one the shopper had to swipe to reach. The wrap
  // clones at either end are not content and are not counted.
  function railCell(node) {
    var slide = node.closest('.agmf-slide, .agmf-hero-slide');
    if (!slide) return { rail_idx: null, rail_len: null, buried: false, is_clone: false };
    var track = slide.parentElement;
    if (!track) return { rail_idx: null, rail_len: null, buried: false, is_clone: false };
    var real = [], i, c;
    for (i = 0; i < track.children.length; i++) {
      c = track.children[i];
      if (c.classList.contains('agmf-slide--clone')) continue;
      var r = c.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) continue;   // a hidden colour deals out
      real.push(c);
    }
    var idx = real.indexOf(slide);
    return {
      rail_idx: idx < 0 ? null : idx,
      rail_len: real.length,
      buried: idx > 0,
      is_clone: slide.classList.contains('agmf-slide--clone')
    };
  }

  function placeOf(node) {
    var card = node.closest('.agmf-card, .agmf-hero-slide') || node;
    var outer = card.closest('.agmf-cycle > *') || card;
    var cell = gridCell(outer);
    var rail = railCell(node);
    var host = node.closest('[data-agmf-idx]');
    var pane = node.closest('.agdesk-media') || document.scrollingElement;
    var r = node.getBoundingClientRect();
    return {
      // THE PLACE, IN ONE NUMBERING (2026-09-22): grid_row / grid_col count
      // the whole feed from 1, grid_cols says how many columns that row had,
      // x is the card down the feed and y the slide across it, both from 1.
      // The block-relative `row`/`col`, the 0-based twins `feed_idx` /
      // `rail_idx` and the feed's own `lap`/`block` are gone: two numberings
      // for one place is how "11 rows only" and "slide 1 = card 1" happened
      // (Dorian: "old events we don't use anymore ... so it doesn't confuse").
      grid_row: cell.grid_row == null ? null : cell.grid_row,
      grid_col: cell.grid_col == null ? null : cell.grid_col,
      grid_cols: cell.of_cols,
      rail_len: rail.rail_len, buried: rail.buried,
      // railCell() has always worked this out and placeOf() dropped it before
      // the wire. A clone is the wrap copy at either end of a rail - the same
      // content twice - so without it a count of a piece's showings is wrong
      // at exactly the edges people swipe through.
      is_clone: rail.is_clone,
      /* DORIAN'S OWN NUMBERING (2026-09-07). It sat beside the 0-based
         fields until 2026-09-22, when those left the wire (see above).
         "3 cards first content would be x=3, y=1 and after
         swapping right 2 times on the same card will be x=3, y=3": so
         x = WHICH CARD down the feed, y = WHICH SLIDE across it, both
         counting from 1. That is the inverse of the usual maths reading of
         x and y, which is exactly why it is written down here. */
      x: (host && host.dataset.agmfIdx) ? Number(host.dataset.agmfIdx) + 1 : null,
      y: rail.rail_idx == null ? null : rail.rail_idx + 1,
      depth_px: Math.round((pane === document.scrollingElement ? window.scrollY : pane.scrollTop) + r.top)
    };
  }

  /* ── impressions and dwell ──────────────────────────────────────────── */

  // The LEAF is the piece of content: a card that has become a rail holds
  // other cards inside its slides, and only the innermost ones were looked
  // at. `.agmf-slide` is in the list because SLIDE 1 IS NOT A CARD - the rail
  // builds it by moving the outer card's children into a bare slide, so
  // without it only the BURIED slides banked any time, which is the exact
  // inverse of the question being asked. Its identity comes from the outer
  // card, which keeps the stamps. The dialog is a unit in its own right.
  function units() {
    var all = document.querySelectorAll('.agmf-card, .agmf-hero-slide, .agmf-slide, .agmf-lb');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].querySelector('.agmf-card')) continue;
      out.push(all[i]);
    }
    return out;
  }

  // THE WINDOW IS NOT THE VIEWPORT ON DESKTOP. The gallery and the feed live
  // inside .agdesk-media, which is its own scroller, and the buy column is
  // another - a card clipped out of a pane still sits inside the window
  // rectangle and would bank time while invisible. The visible box is the
  // window intersected with whichever pane the piece is in.
  function visibleRatio(el) {
    var r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return 0;
    var top = 0, left = 0, bottom = window.innerHeight || 0, right = window.innerWidth || 0;
    var pane = el.closest('.agdesk-media, .product-info.agdesk-buy');
    if (pane) {
      var p = pane.getBoundingClientRect();
      top = Math.max(top, p.top); left = Math.max(left, p.left);
      bottom = Math.min(bottom, p.bottom); right = Math.min(right, p.right);
    }
    var h = Math.max(0, Math.min(r.bottom, bottom) - Math.max(r.top, top));
    var w = Math.max(0, Math.min(r.right, right) - Math.max(r.left, left));
    return (h * w) / (r.height * r.width);
  }

  var visits = {};        // ck -> how many times this page has shown it
  function open(el, id) {
    var k = id.ck || id.record_id || '?';
    visits[k] = (visits[k] || 0) + 1;
    return {
      el: el, ck: id.ck, id: id,
      place: placeOf(el),
      entered_at: Date.now() - t0,
      visit: visits[k],
      ms_on: 0, ms_still: 0, ms_share: 0, ms_still_share: 0, peers: 1, max_pct: 0, parts: 0
    };
  }

  var sent = 0;
  function close(rec, why) {
    if (!rec) return;
    if (rec.ms_on < MIN_VIEW_MS && why !== 'part') { records.delete(rec.el); return; }
    // THE QUEUE IS SHARED WITH THE MONEY. cae-client keeps ONE 500-slot
    // localStorage queue for the whole site and drops the OLDEST on overflow,
    // so an unbounded dwell stream on a 128-card feed could push out
    // session_start or add_to_cart on a slow connection. A page that has
    // already reported this many pieces has said what it has to say.
    if (sent >= MAX_VIEWS) { records.delete(rec.el); return; }
    sent++;
    emit('agmf_content_view', Object.assign({
      surface: surfaceOf(rec.el),
      ms_on_screen: Math.round(rec.ms_on),
      ms_still: Math.round(rec.ms_still),
      ms_share: Math.round(rec.ms_share),
      ms_still_share: Math.round(rec.ms_still_share),
      peers: rec.peers,
      max_visible_pct: Math.round(rec.max_pct * 100),
      entered_ms: rec.entered_at,      // when THIS visit began, ms since load
      visit: rec.visit,                // 1 = first time this page showed it
      ended: why,
      viewport_w: window.innerWidth, viewport_h: window.innerHeight
    }, rec.id, rec.place));
    if (why === 'part') { rec.ms_on = 0; rec.ms_still = 0; rec.ms_share = 0; rec.ms_still_share = 0; rec.parts++; }
    else records.delete(rec.el);
  }

  var lastSample = Date.now();
  function sample() {
    var now = Date.now();
    var dt = Math.min(now - lastSample, SAMPLE_MS * 4);   // a tab that slept banks nothing
    lastSample = now;
    var awake = !document.hidden;

    records.forEach(function (rec) {
      if (!rec.el.isConnected) { close(rec, 'removed'); return; }
      var id = identity(rec.el);
      if (id.ck !== rec.ck) { close(rec, 'recycled'); return; }   // the element took new content
    });

    // TWO PASSES: first who is on screen, then what each one banks - the
    // share a piece gets of this tick depends on who else is there.
    var list = units(), vis = [], sum = 0;
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      var pct = visibleRatio(el);
      var rec = records.get(el);
      if (pct >= SEEN_RATIO) {
        var id = identity(el);
        if (!id.ck && !id.record_id) continue;             // nothing to name it by
        if (!rec) { rec = open(el, id); records.set(el, rec); }
        else if (rec.left) { rec.left = false; }   // came back inside one record
        if (pct > rec.max_pct) rec.max_pct = pct;
        if (awake && !covered(el)) { vis.push({ rec: rec, pct: pct }); sum += pct; }
      } else if (rec && !rec.left) {
        rec.left = true;
        close(rec, 'left');
      }
    }
    var quiet = still();
    for (var v = 0; v < vis.length; v++) {
      var r = vis[v].rec, part = sum > 0 ? vis[v].pct / sum : 0;
      r.ms_on += dt;
      r.ms_share += dt * part;
      if (quiet) { r.ms_still += dt; r.ms_still_share += dt * part; }
      if (vis.length > r.peers) r.peers = vis.length;
      if (r.ms_on >= LONG_VIEW_MS) close(r, 'part');
    }
  }

  function flushAll(why) {
    if (!layout) settleLayout('unload');   // decide now rather than lose a short visit
    records.forEach(function (rec) { close(rec, why || 'unload'); });
    records.clear();
    drainNow();
  }

  /* ── clicks, named ──────────────────────────────────────────────────── */

  // In priority order: the most specific thing the point is inside wins. A
  // name here is a promise to the analyst, so each is a thing a person would
  // recognise on the page rather than a class name.
  var NAMES = [
    ['[data-agmf-sticky-atc]',                 'atc_sticky_bar'],
    // the Add to cart that is always last in the buy column (agmf-buy-tail.js,
    // Dorian 2026-09-08: "track the clicks on it as a new kind of add to
    // cart"). It works by clicking the form's own button, so the echo guard
    // below is what keeps it from also counting as atc_product_form.
    ['[data-agmf-buy-tail-atc]',               'atc_column_end'],
    ['[data-agmf-delivery-atc]',               'atc_delivery_card'],
    ['[data-agmf-sale-atc]',                   'atc_sale_card'],
    ['[data-agmf-sticky-size]',                'sticky_variant_button'],
    ['[data-agmf-sticky]',                     'sticky_bar'],
    ['[data-agmf-chart]',                      'size_chart'],
    ['[data-agmf-choice]',                     'drawer_option'],
    ['[data-agmf-drawer-apply]',               'drawer_apply'],
    ['[data-agmf-drawer-close]',               'drawer_close'],
    ['buy-buttons button, [name="add"], .product-form__submit', 'atc_product_form'],
    ['.shopify-payment-button, [data-shopify="payment-button"]', 'express_checkout'],
    ['.variant-picker__swatch-wrapper, [data-color-value]', 'variant_colour'],
    ['[data-option-value], .variant-picker__option input', 'variant_option'],
    ['.variant-picker__option',                'variant_option'],
    ['quantity-selector, .quantity-selector',  'quantity'],
    ['.agmf-acc--reviews',                     'accordion_reviews'],
    ['.agmf-acc',                              'accordion'],
    ['.agmf-hero-stars, .agmf-hero-rev-n',     'rating_badge'],
    ['.agmf-hero-title',                       'product_title'],
    ['.agmf-sugg-add, .agmf-sugg-cta',         'suggestion_add'],
    ['.agmf-sugg-tile, .agmf-sugg-row',        'suggestion_tile'],
    ['.agmf-sound, [data-agmf-ytsound]',       'sound_toggle'],
    // BOTH play/pause controls, or the centre one falls through to the
    // card_media row below and every press on it is logged as a press on the
    // picture. (The two rows under this one name controls that were cut on
    // 2026-09-09 - they cost nothing and would be needed again if either
    // control comes back.)
    ['[data-agmf-ytplay], [data-agmf-ytresume]', 'video_play_pause'],
    ['[data-agmf-ytbar], [data-agmf-ytclose]', 'youtube_control'],
    // v40.1's two new controls. They MUST sit above the card_media row
    // below: this table is ordered, the row's buttons live inside
    // .agmf-media, and without their own entries a press on either was
    // being logged as a press on the PICTURE - the one signal on a video
    // card that has to stay clean.
    ['[data-agmf-ytfs]',                       'video_fullscreen'],
    ['[data-agmf-ytlink]',                     'youtube_link'],
    ['.agmf-arrow',                            'rail_arrow'],
    ['.agmf-dot',                              'rail_dot'],
    ['.agmf-foot-cue',                         'footer_fold'],
    ['[data-agmf-lb-close]',                   'dialog_close'],
    ['.agmf-lb-side',                          'dialog_text'],
    ['.agmf-cap',                              'caption'],
    ['.agmf-head, .agmf-avatar',               'card_head'],
    ['.agmf-ig-cap, .agmf-caption, .agmf-yt-under', 'card_text'],
    ['.agmf-media, .agmf-review-hero, [data-agmf-yt], video, img', 'card_media'],
    ['.agmf-card, .agmf-hero-slide',           'card'],
    // a section's own words: the feed's setup cards, then any heading on
    // the page (Dorian 2026-09-06: "track clicks on sections - titles")
    ['.agmf-setup-title, .agmf-setup-eyebrow, .agmf-setup-intro', 'section_title'],
    ['h1, h2, h3, h4, .section-header__title, .section__title', 'heading'],
    ['a[href]',                                'link'],
    ['button',                                 'button']
  ];

  // The two add-to-cart buttons, by the name a person would use, and what
  // the form was about to send when one was pressed. The hidden variant
  // input is the truth at that moment; the sticky bar's drawer writes into
  // it on APPLY, so it reads right for that path too.
  var ATC_CONTROL = { atc_sticky_bar: 'sticky_bar', atc_product_form: 'product_form', express_checkout: 'express',
                      atc_column_end: 'column_end',
                      atc_delivery_card: 'delivery_card',
                      atc_sale_card: 'sale_card' };
  function atcForm() {
    var idEl = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    var qEl = document.querySelector('form[action*="/cart/add"] input[name="quantity"], quantity-selector input, .quantity-selector input');
    var id = idEl && idEl.value ? Number(idEl.value) : null;
    var qty = qEl && qEl.value ? Number(qEl.value) : null;
    return { variant_id: isFinite(id) && id ? id : null, quantity: isFinite(qty) && qty ? qty : null };
  }
  var lastAtc = null;        // the last trusted press of an add-to-cart control

  // THE WORDS OF THE THING PRESSED - and our own chrome is not words.
  // Since v40.1 a YouTube card carries a control row inside .agmf-media, so
  // the plain textContent of a picture read "0:00 / 4:09YouTube" plus the
  // close glyph, and every press on a video was labelled with its own player
  // readout (measured on the preview build, 2026-09-09). The row and the
  // close button are lifted out before the words are taken; an aria-label,
  // where there is one, still wins outright.
  function wordsOf(el) {
    if (!el) return '';
    var a = el.getAttribute && el.getAttribute('aria-label');
    if (a) return a;
    if (el.querySelector && el.querySelector('.agmf-ytc, .agmf-yt-close')) {
      try {
        var c = el.cloneNode(true);
        var chrome = c.querySelectorAll('.agmf-ytc, .agmf-yt-close');
        for (var i = 0; i < chrome.length; i++) chrome[i].remove();
        return c.textContent || '';
      } catch (e) {}
    }
    return el.textContent || '';
  }

  function nameFor(target) {
    for (var i = 0; i < NAMES.length; i++) {
      var hit = target.closest(NAMES[i][0]);
      if (hit) return { name: NAMES[i][1], el: hit };
    }
    return null;
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    // THE TWO ADD-TO-CART BUTTONS ARE ONLY TWO IF WE REFUSE THE ECHO. The
    // sticky bar does its work by clicking the real in-column button
    // (agilite-media-flow.js: `mainAtc.click()`), so one tap on the glass bar
    // produced BOTH atc_sticky_bar and a phantom atc_product_form - which is
    // precisely the count Dorian asked to keep apart. Only a click a person
    // actually made is a click; cae-client draws the same line.
    if (e.isTrusted === false) return;
    var hit = nameFor(t);
    if (!hit) return;
    var card = t.closest('.agmf-card, .agmf-hero-slide');
    var detail = {
      target: hit.name,
      surface: surfaceOf(t),
      layout: layoutName(),
      label: wordsOf(hit.el).replace(/\s+/g, ' ').trim().slice(0, 60) || null,
      ms_since_load: Date.now() - t0,
      was_still: still()
    };
    Object.assign(detail, sectionOf(t));
    // A HEADING THAT IS A LINK, OR HOLDS ONE, KEEPS WHERE IT LED - but the
    // fallback searches INSIDE whatever was hit, and since v40.1 every
    // YouTube card carries a watch-page link in its control row. Without
    // the exclusion, every press on a video's picture reported href as a
    // youtube.com URL nobody had clicked. A press on the link itself is
    // unaffected: closest() finds it first and keeps its real href.
    var link = t.closest('a[href]') ||
      (hit.el.querySelector ? hit.el.querySelector('a[href]:not([data-agmf-ytlink])') : null);
    if (link) detail.href = link.getAttribute('href');
    if (ATC_CONTROL[hit.name]) {
      var form = atcForm();
      detail.atc_control = ATC_CONTROL[hit.name];
      detail.variant_id = form.variant_id;
      detail.quantity = form.quantity;
      lastAtc = { control: detail.atc_control, at: Date.now(), variant_id: form.variant_id, quantity: form.quantity };
    }
    if (card) {
      Object.assign(detail, identity(card), placeOf(card));
      var r = card.getBoundingClientRect();
      // where in the picture, so a heat map is possible later
      detail.hit_x_pct = r.width ? Math.round(((e.clientX - r.left) / r.width) * 100) : null;
      detail.hit_y_pct = r.height ? Math.round(((e.clientY - r.top) / r.height) * 100) : null;
    }
    // the variant selector says WHAT was chosen, not just that it was clicked
    if (hit.name === 'variant_colour' || hit.name === 'variant_option') {
      detail.option_value = hit.el.getAttribute('data-color-value') ||
        hit.el.getAttribute('value') || hit.el.getAttribute('title') || detail.label;
      var fs = hit.el.closest('.variant-picker__option');
      var legend = fs && fs.querySelector('legend, .variant-picker__option-info');
      detail.option_name = legend ? legend.textContent.replace(/\s+/g, ' ').trim().slice(0, 40) : null;
    }
    // what a close was, when one follows
    if (hit.name === 'dialog_close') hintClose('close_button');
    else if (t.classList && (t.classList.contains('agmf-lb') || t.classList.contains('agmf-yt-fullscreen'))) hintClose('backdrop');
    emit('agmf_click', detail);
  }, true);

  /* ── THE BIG VIEW: opened, and how it was let go ──────────────────────
     Dorian 2026-09-07: "understand clicks for opening content in full
     screen/dialogs, clicks to close it".

     Measured before building this: `dialog_close` had NEVER fired and
     `surface:'dialog'` appeared 3 times in 3,370 events - the highest-intent
     moment on the page was effectively untracked, because a click that OPENS
     something was recorded as a click on a picture and nothing recorded the
     opening at all.

     Watched rather than wired: the observer sees the dialog appear whoever
     opened it (agmf-desktop-lightbox.js on desktop, expandShell on a phone,
     or anything added later), so no other file has to know this exists and
     no opener can forget to report. HOW it closed is read from the last thing
     the shopper did before it went - the close button, Escape, or a press on
     the backdrop - which is the question "did the content hold them, or did
     they escape it" in the only form the DOM can answer. */
  var bigOpenAt = 0, bigWhat = null, closeHint = null, closeHintAt = 0;

  function hintClose(h) { closeHint = h; closeHintAt = Date.now(); }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') hintClose('escape');
  }, true);

  function bigView() {
    return document.querySelector('.agmf-lb') ||
           document.querySelector('.agmf-yt-fullscreen');
  }

  /* WHICH PIECE WENT BIG (2026-09-23). Measured over 14 days: every
     full-screen open on the phone and 316 of 553 desktop dialog opens
     arrived with no piece, so "Expanded" could be counted but never put
     against a piece (Dorian: "do it"). Two reasons, one per surface:
       - the phone's full screen MOVES the player to <body> and leaves a
         hidden placeholder (`shell.__agmfSlot`) where it was - the card, its
         keys and its place are all still around the placeholder;
       - the desktop dialog stamps its piece's key on itself only when it
         resolved a library record, so the product's own photographs (keyed
         in data-agmf-track-ck on the slide, with no record) opened unnamed.
     So: the placeholder's card first, then the view's own stamp, then the
     card pressed in the last 2.5 s - the press that opened it. */
  var lastPress = null;
  document.addEventListener('pointerdown', function (e) {
    var c = e.target && e.target.closest && e.target.closest('.agmf-card, .agmf-hero-slide');
    lastPress = c ? { at: Date.now(), ident: identity(c), place: placeOf(c) } : null;
  }, true);

  function named(id) { return !!(id && (id.ck || id.record_id)); }

  function bigIdentity(now) {
    var slot = now.__agmfSlot;
    if (slot && slot.parentNode) {
      var fromSlot = identity(slot);
      if (named(fromSlot)) return Object.assign(fromSlot, placeOf(slot.parentNode));
    }
    var own = identity(now);
    if (named(own)) return Object.assign(own, placeOf(now));
    if (lastPress && Date.now() - lastPress.at < 2500 && named(lastPress.ident)) {
      return Object.assign({}, lastPress.ident, lastPress.place);
    }
    return Object.assign(own, placeOf(now));
  }

  function watchBigView() {
    var open = null;
    function look() {
      var now = bigView();
      if (now && !open) {
        open = now;
        bigOpenAt = Date.now();
        closeHint = null;
        bigWhat = Object.assign(
          { kind: now.classList.contains('agmf-lb') ? 'dialog' : 'fullscreen' },
          bigIdentity(now));
        emit('agmf_open', Object.assign({
          layout: layoutName(),
          ms_since_load: bigOpenAt - t0
        }, bigWhat));
      } else if (!now && open) {
        var fresh = closeHint && (Date.now() - closeHintAt) < 1200;
        emit('agmf_close', Object.assign({
          how: fresh ? closeHint : 'other',
          ms_open: Date.now() - bigOpenAt,
          layout: layoutName()
        }, bigWhat || {}));
        open = null; bigWhat = null; closeHint = null;
      }
    }
    try {
      new MutationObserver(look).observe(document.body,
        { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    } catch (e) { /* no observer: opens simply go unrecorded, nothing breaks */ }
    look();
  }
  if (document.body) watchBigView();
  else document.addEventListener('DOMContentLoaded', watchBigView);

  /* ── rails: what the shopper swiped to ──────────────────────────────── */

  function watchRail(track) {
    if (track.__agmfTrackRail) return;
    track.__agmfTrackRail = true;
    // `shownAt` is when the slide being left came to rest, which is how long
    // it held the shopper before they moved off it (Dorian 2026-09-07: "we
    // need a way to detect how much a card has been swiped").
    // FROM WHERE IT WAS RESTING, not from the first scroll event. Capturing
    // the start inside the scroll handler measures from AFTER the movement
    // that fired it: on a programmatic jump that is the whole distance, so
    // moved_px came back 0, and on a real drag it silently loses the first
    // delta. The resting position is known exactly - it is where the rail was
    // when it last came to a stop - so that is what the travel is measured
    // against.
    var at = -1, timer = 0, restingLeft = track.scrollLeft, shownAt = Date.now();
    // A WRAP IS A TELEPORT, NOT A SWIPE (Dorian 2026-09-22: "when we scroll
    // back to left it should decrease by 1 but it is not"). The rails are
    // endless: past the last slide the feed jumps the track back to the
    // first, and before the first it jumps to the last - one instant scroll
    // of the rail's whole width. That read as a gesture: "back, 6 slides,
    // from slide 7 to slide 1" when the finger had gone forward by one.
    // Measured over 7 days: 716 of 1,301 'back' swipes were that jump; every
    // one of the other 585 stepped back by exactly one. A finger cannot
    // move a rail two slide widths inside one scroll event - a hard flick
    // is a run of small ones - so one that does is the jump, and the
    // direction is the finger's, which is the opposite of the track's.
    var lastLeft = track.scrollLeft, jumped = 0;
    track.addEventListener('scroll', function () {
      var nowLeft = track.scrollLeft, delta = nowLeft - lastLeft;
      lastLeft = nowLeft;
      var one = track.children[1] || track.children[0];
      var oneW = one ? one.getBoundingClientRect().width : 0;
      if (oneW > 0 && Math.abs(delta) >= oneW * 1.5) jumped = delta;
      clearTimeout(timer);
      timer = setTimeout(function () {
        // settled: which slide is under the track's left edge now?
        var slides = [], i;
        for (i = 0; i < track.children.length; i++) {
          var r = track.children[i].getBoundingClientRect();
          if (r.width > 1) slides.push({ el: track.children[i], left: r.left });
        }
        if (!slides.length) return;
        var edge = track.getBoundingClientRect().left, best = 0, bd = Infinity;
        slides.forEach(function (s, n) { var d = Math.abs(s.left - edge); if (d < bd) { bd = d; best = n; } });
        if (best === at) { jumped = 0; return; }
        var was = at;
        at = best;
        // THE FIRST SETTLE IS NOT A SWIPE. When a rail is built it comes to
        // rest on its first real slide, and that was reported as a move:
        // measured over 21 days, 566 of 612 agmf_rail_move rows were
        // direction 'start' - 92% of the event was its own exhaust, and the
        // 46 real swipes were buried in it. Nothing did that; a rail existing
        // is not a shopper doing something, and `agmf_content_view` already
        // records that the slide was seen.
        if (was < 0) { shownAt = Date.now(); restingLeft = track.scrollLeft; jumped = 0; return; }
        var wrapped = jumped !== 0;
        var dir = wrapped ? (jumped < 0 ? 'forward' : 'back') : (best > was ? 'forward' : 'back');
        var slide = slides[best].el;
        var inner = slide.querySelector('.agmf-card') || slide;
        // WHO WAS SWIPED AWAY FROM (Dorian 2026-09-07: "we should be able to
        // know how often a content is swapped from - the user sees the
        // content and starts swiping"). The event names the slide arrived AT;
        // the one left behind was only an INDEX, so "how often did people
        // leave this piece" could not be asked of a content id at all. Its
        // identity rides along, and `ms_on_previous` beside it says how long
        // it held them before they went.
        var leftEl = slides[was] && slides[was].el;
        var leftInner = leftEl ? (leftEl.querySelector('.agmf-card') || leftEl) : null;
        var left = leftInner ? identity(leftInner) : {};
        // ONE COORDINATE SPACE, OR THE PAIR IS A TRAP. `was` counts the
        // track's CHILDREN, which include the wrap clones at either end;
        // rail_idx counts REAL slides and skips them. So from_idx and
        // rail_idx were two different numberings under names that read like a
        // pair - a swipe from slide 1 to slide 2 reported from_idx 1,
        // rail_idx 1. The 'from' side is now measured the same way the 'to'
        // side always was: y for where they landed, from_y for where they
        // left (the 0-based twins left the wire on 2026-09-22).
        var leftCell = leftInner ? railCell(leftInner) : { rail_idx: null };
        // HOW FAR, and how long they looked before moving. `moved_px` is the
        // whole travel of the gesture; `moved_slides` is that in slide widths,
        // so a flick past three of them is not filed as one swipe.
        var wide = slides[best].el.getBoundingClientRect().width || 0;
        var moved = Math.round(Math.abs(track.scrollLeft - restingLeft));
        var now = Date.now();
        emit('agmf_rail_move', Object.assign({
          direction: dir,
          seq: ++railSeq,
          from_y: leftCell.rail_idx == null ? null : leftCell.rail_idx + 1,
          from_ck: left.ck || null,
          from_record_id: left.record_id || null,
          from_content_type: left.content_type || null,
          // through a wrap the travel is the jump's, not the finger's: one
          // slide, said so, and the pixels left unsaid rather than made up
          moved_px: wrapped ? null : moved,
          moved_slides: wrapped ? 1 : ((moved != null && wide > 0) ? Math.round((moved / wide) * 100) / 100 : null),
          wrapped: wrapped,
          slide_w: wide ? Math.round(wide) : null,
          ms_on_previous: now - shownAt,
          surface: surfaceOf(track),
          layout: layoutName(),
          ms_since_load: now - t0
        }, identity(inner), placeOf(inner)));
        shownAt = now;
        restingLeft = track.scrollLeft;
        jumped = 0;
      }, 180);
    }, { passive: true });
  }

  /* ── clips ──────────────────────────────────────────────────────────── */

  // A STATE EVENT IS A CHANGE OF STATE (2026-09-07). agmf_video_state was
  // the loudest thing on the page - 1,695 rows from four visitors, ~424
  // each - because a looping autoplay clip reports the same state over and
  // over, and the YouTube bridge repeats it on every info frame. The same
  // state for the same element inside two seconds says nothing new, so it is
  // not sent. A genuine repeat (a replay after a seek) still arrives.
  function stateIsNew(el, state) {
    if (!el) return true;
    var now = Date.now();
    if (el.__agmfState === state && now - (el.__agmfStateAt || 0) < 2000) return false;
    el.__agmfState = state;
    el.__agmfStateAt = now;
    return true;
  }

  function videoState(e, state) {
    var v = e.target;
    if (!v || v.tagName !== 'VIDEO') return;
    if (!stateIsNew(v, state)) return;
    var card = v.closest('.agmf-card, .agmf-hero-slide');
    emit('agmf_video_state', Object.assign({
      state: state,
      muted: !!v.muted,
      at_s: Math.round(v.currentTime || 0),
      dur_s: Math.round(v.duration || 0) || null,
      surface: surfaceOf(v),
      layout: layoutName()
    }, identity(card || v), card ? placeOf(card) : {}));
  }
  // YouTube says what happened even when the shopper used ITS bar: the engine
  // turns the player's state reports into this event (a click inside a
  // cross-origin player is invisible from here, the state it causes is not).
  document.addEventListener('agmf:ytstate', function (e) {
    var shell = e.target;
    var card = shell.closest ? shell.closest('.agmf-card, .agmf-hero-slide') : null;
    var d = e.detail || {};
    if (!stateIsNew(shell, d.state)) return;
    emit('agmf_video_state', Object.assign({
      state: d.state,
      player: 'youtube',
      controls: d.controls,
      at_s: d.at_s,
      dur_s: d.dur_s,
      // attach -> first jsapi message, ms; the boot this shopper waited
      boot_ms: (window.AGMF && window.AGMF.ytBootMs) ? (window.AGMF.ytBootMs() || null) : null,
      surface: surfaceOf(shell),
      layout: layoutName()
    }, identity(card || shell), card ? placeOf(card) : {}));
  }, true);

  document.addEventListener('play', function (e) { videoState(e, 'play'); }, true);
  document.addEventListener('pause', function (e) { videoState(e, 'pause'); }, true);
  document.addEventListener('ended', function (e) { videoState(e, 'ended'); }, true);

  /* ── the add itself, by the button that made it ─────────────────────── */

  // The theme announces an add two ways - `variant:add` from its product
  // form, and `cart:change` from the sections that add on their own - and
  // one add can arrive by both. They are folded on the variant within a few
  // seconds. A `cart:change` with no press behind it is a quantity change
  // or someone else's add, not ours to attribute, and is left alone.
  var drawerAppliedAt = 0, lastAddKey = '', lastAddAt = 0;
  function onAdd(via, e, extra) {
    var d = (e && e.detail) || {};
    var lines = Array.isArray(d.items) ? d.items : (d.item ? [d.item] : []);
    var line = lines[0] || null;
    var cart = d.cart || null;
    if (!line && cart && cart.items && cart.items.length) line = cart.items[cart.items.length - 1];
    var variantId = (line && (line.variant_id || line.id)) || (d.variant && d.variant.id) || d.variantId || null;
    var now = Date.now();
    var recent = lastAtc && now - lastAtc.at < 5000 ? lastAtc : null;
    if (via === 'cart:change' && !recent) return;
    var key = String(variantId || (recent && recent.variant_id) || '?');
    if (key === lastAddKey && now - lastAddAt < 3000) return;
    lastAddKey = key; lastAddAt = now;
    var open = [];
    records.forEach(function (r) { if (r.ck && open.length < 3) open.push(r.ck); });
    emit('agmf_add_to_cart', Object.assign({
      control: recent ? recent.control : 'other',
      variant_id: variantId || (recent && recent.variant_id) || null,
      quantity: (line && line.quantity) || (recent && recent.quantity) || null,
      via: via,
      ms_since_press: recent ? now - recent.at : null,
      from_drawer: drawerAppliedAt > 0 && now - drawerAppliedAt < 60000,
      on_screen: open,
      layout: layoutName(),
      ms_since_load: now - t0
    }, extra || {}));
  }
  window.addEventListener('variant:add', function (e) { onAdd('variant:add', e); });

  /* ── THE CART IS THE TRUTH, NOT A CUSTOM EVENT ────────────────────────
     agmf_add_to_cart had not fired ONCE in 21 days, and the reason was not
     traffic: it waited for `variant:add`, and the only thing in this theme
     that dispatches that is the Ask card (agmf-ask-card.js, the sole match in
     assets/). The product form's own button and the sticky bar that clicks it
     announce nothing at all, so the event could only ever describe adds from
     one small surface - and the two buttons Dorian specifically asked to tell
     apart were both invisible.

     Every add, whoever makes it and whatever it dispatches, is a POST to
     /cart/add. That is what is watched now, so the event cannot be missed by
     a code path that forgot to speak.

     IT MUST NEVER COST A SHOPPER THEIR CART. The wrapper calls straight
     through and returns the original promise whatever happens; it only ever
     reads a CLONE of the response, and every step is inside try/catch.
     session-overrides.js already wraps fetch on this page, so a wrapper here
     is a pattern the page survives, not a new risk. */
  (function watchTheCart() {
    function isAdd(u) {
      return typeof u === 'string' && u.indexOf('/cart/add') !== -1;
    }
    // /cart/add.js answers in two shapes: `{items:[...]}` for a multi-line
    // post, and the LINE ITEM ITSELF for a single one. Handing the whole body
    // over as `item` made the first shape look like a line with no id, so the
    // event arrived with a null variant and quantity - measured on the first
    // real add. Each shape is unwrapped into what onAdd already reads.
    function told(body) {
      var detail = {};
      try {
        if (body && Array.isArray(body.items)) detail.items = body.items;
        else if (body && (body.id || body.variant_id)) detail.item = body;
      } catch (e) { /* an unreadable body is still an add */ }
      try { onAdd('cart/add', { detail: detail }); } catch (e) { /* never */ }
    }
    try {
      var realFetch = window.fetch;
      if (typeof realFetch === 'function') {
        window.fetch = function (input, init) {
          var url = '';
          try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
          var out = realFetch.apply(this, arguments);
          if (isAdd(url)) {
            try {
              out.then(function (res) {
                try {
                  if (!res || !res.ok) return;
                  res.clone().json().then(told, function () { told(null); });
                } catch (e) { /* a body we cannot read is still an add */ told(null); }
              }, function () { /* the add failed: nothing to report */ });
            } catch (e) {}
          }
          return out;
        };
      }
    } catch (e) { /* no fetch to wrap */ }
    try {
      var open = XMLHttpRequest.prototype.open;
      var send = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (m, u) {
        try { this.__agmfAdd = isAdd(u); } catch (e) {}
        return open.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        try {
          if (xhr.__agmfAdd) {
            xhr.addEventListener('load', function () {
              if (xhr.status < 200 || xhr.status >= 300) return;
              var body = null;
              try { body = JSON.parse(xhr.responseText || 'null'); } catch (e) {}
              told(body);
            });
          }
        } catch (e) {}
        return send.apply(this, arguments);
      };
    } catch (e) { /* the form may not use XHR at all */ }
  }());

  // THE ASK CARD'S OWN BUTTON. agmf-ask-card.js never touches this file: it
  // announces what happened as agmf:ask (offer shown, pressed, added, or the
  // cart refused) and the rows are made here, in the same shape as every
  // other add - control says which button, qa_id joins the row to the chat
  // log. A press is recorded as the last trusted press, so a cart:change the
  // theme may fire for the same add folds into ONE row instead of two.
  document.addEventListener('agmf:ask', function (e) {
    var d = (e && e.detail) || {};
    var now = Date.now();
    // place says WHICH card: 'feed' (inside the media feed) or 'inline'
    // (under Add to Cart, 2026-09-14) - two cards, two conversations
    var base = { qa_id: d.qa_id || null, variant_id: d.variant_id || null, place: d.place || null,
                 layout: layoutName(), ms_since_load: now - t0 };
    // THREE KINDS OF ADD TO CART on a product page, told apart in every row
    // (Dorian 2026-09-07: "let's review how we separate the 3 kinds"):
    //   control = 'ask_card', ask_role = 'product'  the chat's button for THIS product
    //   control = 'ask_card', ask_role = 'addon'    the chat's "Complete your setup" rows
    //   control = anything else (form / sticky ...) the page's own buttons, no ask_role
    // qa_id joins a chat add to the question that earned it.
    if (d.kind === 'shown') {
      // the card was at least half on screen, once per card per page -
      // what "nobody used it" is measured against (2026-09-23)
      emit('agmf_ask_shown', Object.assign({ slot: d.slot == null ? null : d.slot }, base));
      return;
    }
    if (d.kind === 'offer') {
      emit('agmf_ask_offer', Object.assign({ kind: 'product', ask_role: 'product',
                                             title: d.title || null, price: d.price == null ? null : d.price }, base));
    } else if (d.kind === 'press') {
      lastAtc = { control: 'ask_card', at: now, variant_id: d.variant_id || null, quantity: 1 };
      emit('agmf_ask_click', Object.assign({ target: 'ask_buy', ask_role: 'product', label: d.title || null }, base));
    } else if (d.kind === 'added') {
      lastAtc = lastAtc && lastAtc.control === 'ask_card' ? lastAtc
              : { control: 'ask_card', at: now, variant_id: d.variant_id || null, quantity: 1 };
      onAdd('ask_card', { detail: { items: [{ variant_id: d.variant_id || null, quantity: d.quantity || 1 }] } },
            { qa_id: d.qa_id || null, ask_role: 'product' });
    } else if (d.kind === 'add_failed') {
      emit('agmf_ask_add_failed', Object.assign({ ask_role: 'product' }, base));
    } else if (d.kind === 'reply') {
      // a tap-to-answer chip: which one, for which question
      emit('agmf_ask_click', Object.assign({ target: 'ask_reply', label: d.label || null }, base));
    } else if (d.kind === 'checkout') {
      emit('agmf_ask_click', Object.assign({ target: 'ask_checkout' }, base));
    } else if (d.kind === 'support') {
      // the support link under a refusal: a question the page could not
      // answer AND a shopper who went looking for a person
      emit('agmf_ask_click', Object.assign({ target: 'ask_support' }, base));
    } else if (d.kind === 'addon_offer') {
      // the "Complete your setup" rows under an answer: which accessories,
      // as handles, and how many
      emit('agmf_ask_offer', Object.assign({ kind: 'addon', ask_role: 'addon', title: (d.handles || []).join(','),
                                             price: null, count: d.count || 0 }, base));
    } else if (d.kind === 'addon_press') {
      emit('agmf_ask_click', Object.assign({ target: 'ask_addon_add', ask_role: 'addon', label: String((d.variant_ids || []).length) }, base));
    } else if (d.kind === 'addon_added') {
      // one add row per line, the way the theme's own batch adds read
      (d.variant_ids || []).forEach(function (id) {
        lastAtc = { control: 'ask_card', at: now, variant_id: id, quantity: 1 };
        onAdd('ask_card', { detail: { items: [{ variant_id: id, quantity: 1 }] } },
              { qa_id: d.qa_id || null, ask_role: 'addon' });
      });
    } else if (d.kind === 'addon_add_failed') {
      emit('agmf_ask_add_failed', Object.assign({ ask_role: 'addon' }, base));
    } else if (d.kind === 'asked') {
      // a question went out from the card - the words are in the chat log,
      // the fact is here, per visit and layout (2026-09-29)
      emit('agmf_ask_question', Object.assign({ turn: d.turn == null ? null : d.turn }, base));
      return;
    } else if (d.kind === 'focus') {
      // a tap on the card's input on a touch device: did the keyboard come,
      // and if not, what took the focus (Dorian 2026-09-08: no keyboard on
      // real phones, fine in Chrome's emulation)
      emit('agmf_ask_focus', Object.assign({ tap: d.tap || null, focused: !!d.focused, stolen_by: d.stolen_by || null,
                                             connected: !!d.connected, vh_before: d.vh_before || null,
                                             vh_after: d.vh_after || null, vv: d.vv || null }, base));
    }
  });
  // THE DELIVERY CARD, ON SCREEN (2026-09-29). agmf-delivery-card.js never
  // touches this file; it announces agmf:delivery when a card is at least
  // half on screen, once per card, and the row is made here - the measure a
  // press of its Add to cart (atc_delivery_card) is read against.
  document.addEventListener('agmf:delivery', function (e) {
    var d = (e && e.detail) || {};
    if (d.kind !== 'shown') return;
    emit('agmf_delivery_shown', { slot: d.slot == null ? null : d.slot, place: 'feed',
                                  layout: layoutName(), ms_since_load: Date.now() - t0 });
  });

  // the same, for the sale card (atc_sale_card). It only ever appears when a
  // campaign is on AND this variant is discounted, so the count is also the
  // answer to "how often did either of those not hold".
  document.addEventListener('agmf:sale', function (e) {
    var d = (e && e.detail) || {};
    if (d.kind !== 'shown') return;
    emit('agmf_sale_shown', { slot: d.slot == null ? null : d.slot, place: 'feed',
                              layout: layoutName(), ms_since_load: Date.now() - t0 });
  });

  function onCartChange(e) {
    var be = (e && e.detail && e.detail.baseEvent) || '';
    if (be !== 'add' && be !== 'variant:add' && be !== '') return;
    onAdd('cart:change', e);
  }
  window.addEventListener('cart:change', onCartChange);
  document.addEventListener('cart:change', onCartChange);

  /* ── the sticky bar's variant drawer ────────────────────────────────── */

  // The engine says what happened in the sheet - opened, a choice, applied,
  // refused - and the wire is ours, as with the heart. A refusal names why:
  // that combination does not exist, or it is sold out. Both are the
  // shopper telling us what they wanted and could not buy.
  document.addEventListener('agmf:variant', function (e) {
    var d = e.detail || {};
    if (d.step === 'apply' && d.ok) drawerAppliedAt = Date.now();
    emit('agmf_variant_drawer', Object.assign({
      surface: 'sticky_bar',
      layout: layoutName(),
      ms_since_load: Date.now() - t0
    }, d));
  }, true);

  /* ── an Agilike ──────────────────────────────────────────────────────── */

  // The engine draws the heart and says so; the wire is ours. `who liked` is
  // the envelope's own viewer and session ids, which is why this rides the
  // same emitter as everything else rather than inventing a call of its own.
  document.addEventListener('agmf:agilike', function (e) {
    var host = e.target;
    var d = e.detail || {};
    emit('agmf_agilike', Object.assign({
      surface: surfaceOf(host),
      layout: layoutName(),
      ms_since_load: Date.now() - t0
    }, identity(host), d, placeOf(host)));
  }, true);

  /* ── dead media ─────────────────────────────────────────────────────── */
  // The engine removed a piece that errored (deleted video, gone file,
  // broken picture) and said so; the record id on the wire is how Agilite
  // Media learns it. Same envelope as the agilike.
  // A DEPARTURE TO YOUTUBE.COM. The engine INFERS this - focus was inside
  // the player, then the page went away - because a cross-origin player
  // never reports its own clicks. `how` separates a new tab from a real
  // exit, so nobody reads an inference as a click we actually saw.
  document.addEventListener('agmf:yt_exit', function (e) {
    var host = e.target;
    var d = e.detail || {};
    emit('agmf_yt_exit', Object.assign({
      surface: surfaceOf(host),
      layout: layoutName(),
      ms_since_load: Date.now() - t0
    }, identity(host), d, placeOf(host)));
  }, true);

  document.addEventListener('agmf:media_error', function (e) {
    var host = e.target;
    var d = e.detail || {};
    emit('agmf_media_error', Object.assign({
      surface: surfaceOf(host),
      layout: layoutName(),
      ms_since_load: Date.now() - t0
    }, identity(host), d, placeOf(host)));
  }, true);
  /* ── scrolling, of every kind ───────────────────────────────────────── */

  function moved() { lastScrollAt = Date.now(); }
  window.addEventListener('scroll', moved, { passive: true });
  window.addEventListener('wheel', moved, { passive: true });
  window.addEventListener('touchmove', moved, { passive: true });
  // the desktop panes and every rail are their own scrollers and never reach
  // the window listener; capture catches all of them with one handler.
  document.addEventListener('scroll', function (e) {
    moved();
    var t = e.target;
    if (t && t.classList && t.classList.contains('agmf-track')) watchRail(t);
  }, true);

  /* ── run ────────────────────────────────────────────────────────────── */

  setInterval(sample, SAMPLE_MS);
  setInterval(function () {
    document.querySelectorAll('.agmf-track, .agmf-hero-track').forEach(watchRail);
  }, 2000);

  // ONE WAY OUT. Queueing the last rows is ours; SENDING them is cae-client's
  // pagehide beacon, which runs after this one. Calling its flushNow() here
  // sent the same rows a second time through a non-beacon fetch, so every
  // final row and every unload-time click landed twice in the raw table -
  // invisible in events_dedup, doubled for anyone reading the table itself.
  // And beforeunload was a third copy of the same work.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) flushAll('hidden');
  });
  window.addEventListener('pagehide', function () { flushAll('unload'); });

  window.AGMFTrack = {
    open: function () { return Array.from(records.values()).map(function (r) {
      return { ck: r.ck, ms_on: Math.round(r.ms_on), ms_still: Math.round(r.ms_still), place: r.place };
    }); },
    flush: flushAll,
    still: still
  };
}());
